import ServiceExec from '@/common/utils/ServiceExec';
import { user } from '@/common/service/apiList';
import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME } from '@/common/constants/ConstCode';
import { asFormData, fileRequestBinding, isEmpty, REMOVE_EMPTY_PARAMS } from '@/common/utils/Util';
import store, { resetStoreOnLogout } from '@/store';
import { cloneDeep } from 'lodash';
import qs from 'query-string';
const {
  signUp,
  logIn,
  logOut,
  deleteUser,
  canDeleteUser,
  renewAccessToken,
  authCode,
  familyCodeSearch,
  emailDuplicated,
  findId,
  editPassword,
  updateUser,
  findIdAuthCode,
  joinAuthCode,
  findPasswordCode,
  userItemCount,
  userFamilyList,
  editMarketing,
  deathCertificate,
  userIdExists,
  renewFcmToken,
  editBusinessMarketing,
  premiumNumberCheck,
  updateUserPremium,
  versionCheck,
  smsVerify
} = user;

export default {
  signUp: async ({ commit, getters }, { isFamilyRepresent, joinType, eventCode }) => {
    const payload = cloneDeep(getters['getSignUp']);

    payload.form.isFamilyRepresent = isFamilyRepresent;
    payload.form.joinType = joinType;
    if (eventCode) payload.form.eventCode = eventCode;   // 260619 새 모델: 업체코드 BE 전달(신규회원 귀속 업체 기록)
    payload.form.deviceInfo = getters['getDeviceInfo'];
    payload.form.isPremium = getters['getIsPremium'];
    payload.form.couponNumber = getters['getCouponNumber'];

    // 260513 리텐션 보드: 채널 ROI용 utm_source 첨부 (App.vue onMounted에서 캐치 후 localStorage 저장)
    try {
      const utm = window.localStorage.getItem('jump_utm_source');
      if (utm) payload.form.utmSource = utm;
    } catch (_) {
      /* localStorage 차단 환경 무시 */
    }

    delete payload.form?.email;
    const { status } = await ServiceExec.post(signUp, REMOVE_EMPTY_PARAMS(payload.form));
    if (status === ServiceExec.status.OK) {
      console.log('회원가입 성공', payload.form);
      await store.dispatch('user/signIn', payload.form);
      return true;
    }
    return false;
  },
  signIn: async ({ commit, getters }, form) => {
    form.deviceInfo = null;
    const { status, data, headers } = await ServiceExec.patch(logIn, form);
    if (status !== ServiceExec.status.OK) return false;

    commit('storeLogin', {
      userData: data,
      accessToken: headers[ACCESS_TOKEN_NAME],
      refreshToken: headers[REFRESH_TOKEN_NAME]
    });

    const deviceInfo = getters['getDeviceInfo'];
    deviceInfo.userNo = data.userNo;
    console.log('디바이스', deviceInfo);
    commit('setDeviceInfo', deviceInfo);
    store.commit('user/setFirstLogin', true);
    return true;
  },

  // 진행중 초대관/장례식장 유무 판정은 BE만 신뢰한다.
  // 로그인 정보 부재/네트워크/인증 오류는 "초대관·장례식장 있음"과 무관하므로 false로 뭉뚱그리지 않고
  // throw로 호출부에 전파해 정확한 안내를 띄우게 한다. (false 폴백 시 오탐 차단 발생 — 260623)
  canDeleteUser: async ({ getters }) => {
    const { userNo } = getters['getUserData'];
    if (isEmpty(userNo)) throw new Error('canDeleteUser: missing userNo');

    const response = await ServiceExec.get(canDeleteUser(userNo));
    return response?.data === true;
  },

  deleteUser: async ({ commit, getters }, reason) => {
    const { userNo } = getters['getUserData'];
    const { status, data } = await ServiceExec.post(deleteUser, { userNo, reason });
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },

  // 260424 오후: 회원가입/소셜 가입 직후 관심 카테고리 저장
  saveInterestCategories: async (_, categoryNos) => {
    const { status, data } = await ServiceExec.post(
      '/api/user/interest-category',
      { categoryNos: Array.isArray(categoryNos) ? categoryNos : [] }
    );
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },

  // 260630 C: 관심사 설문 상태/prefill. {completed, categoryNos, answers}. 가입 직후 프롬프트 판정용.
  getInterestSurvey: async () => {
    try {
      const { status, data } = await ServiceExec.get('/api/user/interest-category/survey');
      if (status !== ServiceExec.status.OK) return null;
      return data ?? null;
    } catch (e) { return null; }
  },

  // 260630 C: 관심사 설문 저장. answers(취향)만 보내면 관심 업종은 유지(가입 step7 수집분). categoryNos 보내면 교체.
  saveInterestSurvey: async (_, { categoryNos = null, answers = {} }) => {
    try {
      const body = { answers };
      if (Array.isArray(categoryNos)) body.categoryNos = categoryNos;
      const { status, data } = await ServiceExec.post('/api/user/interest-category/survey', body);
      if (status !== ServiceExec.status.OK) return null;
      return data ?? null;
    } catch (e) { return null; }
  },

  // 260622: 내 위치(좌표) 저장 — 반경 전단지 발송 타겟용
  saveMyLocation: async ({ getters }, { latitude, longitude }) => {
    const userNo = getters['getUserData']?.userNo;
    if (!userNo) return false;
    const { status } = await ServiceExec.post(`/api/user/me/location?userNo=${userNo}&latitude=${latitude}&longitude=${longitude}`);
    return status === ServiceExec.status.OK;
  },

  signOut: async ({ commit, getters }) => {
    const userData = getters['getUserData'];
    const deviceInfo = getters['getDeviceInfo'];
    const payload = {
      deviceInfo: deviceInfo,
      userNo: userData.userNo
    };

    const { status } = await ServiceExec.patch(logOut, payload);
    if (status !== ServiceExec.status.OK) return;
    commit('storeLogout');
    commit('setDeviceInfo');
    // 260515: 다른 도메인 모듈(business/card/memorial 등) state도 초기화. 계정 전환 시 누수 방지.
    resetStoreOnLogout();
  },
  getAuthCode: async ({ commit, getters }) => {
    const phone = getters['getPhone'];
    const user = getters['getUserData'];

    const payload = {
      phone: phone,
      userNo: user?.userNo || null
    };
    const { status, data } = await ServiceExec.get('/api/user/auth/phone', payload);
    if (status !== ServiceExec.status.OK) return data;
    commit('setAuthCode', data);
    return true;
  },

  joinAuthCode: async ({ commit, getters }) => {
    const { phone, code } = getters['getPhoneInfo'];
    const { status, data } = await ServiceExec.get(joinAuthCode, { phone, code });
    if (status !== ServiceExec.status.OK) return data;
    // 260710 P0: 응답은 이제 Boolean(true) — 코드는 서버에만 저장. 클라 비교 폐지, 검증은 verifyPhoneCode로.
    return true;
  },

  // 260710 P0: 인증번호 서버 검증 — PhoneAuthInput 공통. 발송 방식(가입/찾기/변경) 무관하게 phone+입력코드로 서버 대조.
  verifyPhoneCode: async ({ getters }, inputCode) => {
    const { phone, code } = getters['getPhoneInfo'];
    const { status, data } = await ServiceExec.post(smsVerify, { phone, code, authCode: inputCode });
    return status === ServiceExec.status.OK && data === true;
  },

  updatePhoneAuthCode: async ({ commit, getters }, payload) => {
    const { userNo } = getters['getUserData'];
    const { status, data } = await ServiceExec.get(authCode, {
      phone: payload.phone,
      code: payload.code,
      userNo
    });
    if (status !== ServiceExec.status.OK) return data;
    commit('setAuthCode', data);
    return true;
  },

  findIdAuthCode: async ({ commit, getters }, payload) => {
    const phone = getters['getPhone'];
    const { status, data } = await ServiceExec.get(findIdAuthCode, payload);
    if (status !== ServiceExec.status.OK) return data;
    commit('setAuthCode', data);
    return true;
  },
  findPasswordAuth: async ({ commit, getters }, payload) => {
    console.log('findPasswordAuth :: ', payload);
    const { userId } = getters['getFindData'];
    const { status, data } = await ServiceExec.get(findPasswordCode, {
      ...payload,
      userId
    });
    if (status !== ServiceExec.status.OK) return data;
    commit('setAuthCode', data);
    return true;
  },
  renewAccessToken: async ({ commit, getters }) => {
    const { authNo } = getters['getUserData'] || '';
    const accessToken = getters['getAccessToken'] || '';
    const refreshToken = getters['getRefreshToken'] || '';

    if (!authNo || !accessToken || !refreshToken) return false;
    const { status, headers } = await ServiceExec.patch(renewAccessToken, { authNo, accessToken, refreshToken });
    if (status !== ServiceExec.status.OK) return false;

    commit('setAccessToken', headers[ACCESS_TOKEN_NAME]);
    commit('setRefreshToken', headers[REFRESH_TOKEN_NAME]);
    commit('ttlInit');
    return true;
  },
  renewFcmToken: async ({ commit, getters }) => {
    const device = getters['getDeviceInfo'];
    const userData = getters['getUserData'];
    console.log('토큰 갱신', device);

    device.userNo = userData.userNo;
    const { status, data } = await ServiceExec.patch(renewFcmToken, device);
    if (status !== ServiceExec.status.OK) return false;
    commit('setDeviceInfo', data);
    return true;
  },

  familyCodeSearch: async ({ commit, getters, state }, searchText) => {
    const payload = {
      searchText: searchText
    };
    const { status, data } = await ServiceExec.get(familyCodeSearch, payload);
    if (!status === ServiceExec.status.OK) return false;
    commit('setFamilyList', data.list ?? []);
  },
  emailDuplicated: async ({ commit, state }, email) => {
    if (isEmpty(email)) return false;
    const { status, data } = await ServiceExec.get(emailDuplicated, { email });
    if (!status === ServiceExec.status.OK) return false;
    commit('setDuplicated', data);
  },
  findId: async ({ commit, state, getters }) => {
    if (isEmpty(state.phone)) return false;

    const payload = {
      phone: state.phone,
      code: state.code
    };
    const { status, data } = await ServiceExec.get(findId, payload);
    if (status !== ServiceExec.status.OK || isEmpty(data)) return false;
    commit('setFindData', data);
    return true;
  },

  findAccounts: async ({ commit, state, getters }) => {
    if (isEmpty(state.phone)) return false;

    const payload = {
      phone: state.phone,
      code: state.code
    };
    const { status, data } = await ServiceExec.get(findId, payload);
    if (status !== ServiceExec.status.OK || isEmpty(data)) return false;
    commit('setFindAccounts', data);
    return true;
  },

  editUser: async ({ state, getters, commit }, payload) => {
    if (isEmpty(payload)) return false;
    const { userNo } = getters['getUserData'];
    payload.userNo = userNo;
    // const reqParams = new URLSearchParams(payload);
    const { status } = await ServiceExec.patch(updateUser, payload);
    if (status === ServiceExec.status.OK) {
      commit('setUserData', { ...getters['getUserData'], ...payload });
      return true;
    }
  },

  /**
   * 비밀번호 변경
   * @param state
   * @param getters
   * @param password
   * @return {Promise<boolean>}
   */
  editPassword: async ({ state, getters }, payload) => {
    if (isEmpty(payload.password)) return false;

    const form = getters['getFindData'];
    const { phone, userId } = form;

    const { status, data } = await ServiceExec.put(editPassword, payload);
    return status === ServiceExec.status.OK;
  },

  /**
   * 비밀번호 변경
   * @param state
   * @param getters
   * @param password
   * @return {Promise<boolean>}
   */
  editPasswordInMyPage: async ({ state, getters }, password) => {
    if (isEmpty(password)) return false;

    const user = getters['getUserData'];
    const { phone, userId } = user;

    const { status, data } = await ServiceExec.put(editPassword, { password: password, phone: phone, userId: userId });
    return status === ServiceExec.status.OK;
  },

  getItemCount: async ({ state, getters }) => {
    const { userNo } = getters['getUserData'];
    if (isEmpty(userNo)) return false;
    const { status, data } = await ServiceExec.get(userItemCount + userNo);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  getFamilyList: async ({ state, commit, getters }) => {
    const { userNo } = getters['getUserData'];
    const { status, data } = await ServiceExec.get(userFamilyList + userNo);
    if (status !== ServiceExec.status.OK) return false;
    commit('setFamilyList', data);
    return true;
  },
  editAgreePush: async ({ state, commit, getters }, isAgree) => {
    const { userNo } = getters['getUserData'];
    const { status } = await ServiceExec.put(editMarketing + userNo + `?isAgree=${isAgree}`);
    return status === ServiceExec.status.OK;
  },

  editAgreeBusinessMarketingPush: async ({ state, commit, getters }, isAgree) => {
    const { userNo } = getters['getUserData'];
    const { status } = await ServiceExec.put(editBusinessMarketing + userNo + `?isAgree=${isAgree}`);
    return status === ServiceExec.status.OK;
  },
  deathCertificate: async ({ state, commit, getters }, payload) => {
    const { userNo } = getters['getUserData'];
    const reqData = cloneDeep({ ...payload, userNo });
    fileRequestBinding(reqData);
    const formData = asFormData(reqData);
    const { status } = await ServiceExec.multiPost(deathCertificate, formData);
    return status === ServiceExec.status.OK;
  },

  userIdExists: async ({ state, commit }, userId) => {
    const { data, status } = await ServiceExec.get(userIdExists, { userId: userId });
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },

  getIsUnRead: async ({ state, commit, getters }) => {
    const { userNo } = getters['getUserData'];
    if (isEmpty(userNo)) return false;

    const response = await ServiceExec.get('/api/notification/unread', { userNo });
    if (response?.status !== ServiceExec.status.OK) return false;
    commit('updateReadState', !response?.data);
    return true;
  },
  premiumNumberCheck: async ({ state }, couponNumber) => {
    const { data, status } = await ServiceExec.get(premiumNumberCheck(couponNumber));
    return status === ServiceExec.status.OK && data;
  },

  updateUserPremium: async ({ state }, payload) => {
    const { data, status } = await ServiceExec.get(updateUserPremium(payload.userNo, payload.couponNumber));
    return status === ServiceExec.status.OK && data;
  },
  versionCheck: async ({ state }, payload) => {
    const { data, status } = await ServiceExec.get(versionCheck, payload);
    return data;
  }
};
